import * as Yup from 'yup'
import { FormikErrors, yupToFormErrors } from 'formik'
import { hasHtmlText } from '../../../../../../utils/sanitizeRichText'
import { projectObjectivesRule, projectOutcomesRule, smartIndicatorsRule } from '../../ProjectInitiation-steps/core/_validation'
import { BENEFIT_CATEGORIES, JUSTIFICATION_TABS, MARKET_ANALYSIS_TABS, OBJECTIVES_TABS, SECTOR_ANALYSIS_TABS, STEP_TABS, STRATEGIC_FUTURES_TABS, STRATEGIC_SIGNIFICANCE_TABS } from './_models'

export const FUTURE_DEMAND_GROWTH_DESCRIPTION_MAX_LENGTH = 255

export const maxCharsMessage = (label: string, maxLength: number): string =>
    `${label} must be ${maxLength} characters or less`

const requiredString = (label: string, maxLength?: number) => {
    let schema = Yup.string().trim().required().label(label)

    if (maxLength !== undefined) {
        schema = schema.max(maxLength, maxCharsMessage(label, maxLength))
    }

    return schema
}

const requiredNumber = (label: string) =>
    Yup.number()
        .required()
        .label(label)

const requiredArray = (label: string, min = 1) =>
    Yup.array()
        .of(Yup.string())
        .min(min, `At least ${min} ${label} must be selected`)
        .required()

// Editor HTML that must hold text, not just empty tags.
const requiredRichText = (label: string) =>
    Yup.string()
        .test('has-text', `${label} is a required field`, hasHtmlText)
        .label(label)

// Every selected option in `optionsField` needs a remark (the editor's HTML must hold text).
const remarkPerOption = (optionsField: string) =>
    Yup.object().test('option-remarks', 'Remarks are required', function (remarks) {
        const selected = (this.parent[optionsField] ?? []) as string[]
        const missing = selected.find((key) => !hasHtmlText((remarks as Record<string, string> | undefined)?.[key]))
        return missing
            ? this.createError({ path: `${this.path}.${missing}`, message: 'Remark is required' })
            : true
    })

// Tab-wise steps save each tab on its own, so only the open tab is validated. Keyed by step, then tab id.
export const stepTabSchemas: Partial<Record<string, Record<number, Yup.AnyObjectSchema>>> = {
    benefits_and_desired_outcomes: Object.fromEntries(BENEFIT_CATEGORIES.map((category) => [
        category.data_value,
        Yup.object({
            benefit_outcomes: Yup.object({
                [category.data_key]: Yup.array().of(Yup.object({
                    outcome_id: Yup.number().required(),
                    sub_categories: Yup.array().of(Yup.string().required()),
                }))
                    .required(`Assign at least one project outcome to ${category.display_name}.`)
                    .min(1, `Assign at least one project outcome to ${category.display_name}.`),
            }),
        }),
    ])),
    objectives: {
        [OBJECTIVES_TABS.project_objectives]: Yup.object({ project_objectives_areas: projectObjectivesRule() }),
        [OBJECTIVES_TABS.project_outcomes]: Yup.object({ project_outcomes: projectOutcomesRule() }),
        [OBJECTIVES_TABS.smart_indicators]: Yup.object({ smart_indicators: smartIndicatorsRule() }),
    },
    // Root Causes has no schema: its causes save from their own popup.
    justification: {
        [JUSTIFICATION_TABS.needs_analysis]: Yup.object({
            problem_statement: requiredString('Problem Statement'),
            affected_population: requiredArray('Affected Population'),
            affected_population_remarks: remarkPerOption('affected_population'),
            severity_level: requiredString('Severity Level'),
            consequences_of_no_action: requiredString('Consequences of No Action'),
            existing_services_baseline: requiredString('Existing Services'),
            justification_quantified_status_quo: requiredString('Quantified Status Quo'),
        }),
        [JUSTIFICATION_TABS.demand_analysis]: Yup.object({
            current_demand: requiredString('Current Demand'),
            future_demand_growth_rate: requiredNumber('Future Demand Growth Rate'),
            future_demand_growth_description: requiredString(
                'Future Demand Growth Description',
                FUTURE_DEMAND_GROWTH_DESCRIPTION_MAX_LENGTH
            ),
            supply_demand_gap: requiredString('Supply-Demand Gap'),
            demand_data_sources: requiredArray('Data Source', 2),
            forecast_horizon: requiredString('Forecast Horizon'),
        }),
    },
    market_size_and_demand_analysis: {
        [MARKET_ANALYSIS_TABS.market_size]: Yup.object({
            market_size: requiredRichText('Market Size'),
        }),
        [MARKET_ANALYSIS_TABS.target_market]: Yup.object({
            target_market: requiredRichText('Target Market'),
        }),
        [MARKET_ANALYSIS_TABS.market_growth_and_gap]: Yup.object({
            market_growth_rate: requiredNumber('Market Growth Rate')
                .min(0, 'Market Growth Rate cannot be negative')
                .max(100, 'Market Growth Rate cannot be greater than 100'),
            market_gap: requiredRichText('Market Gap'),
        }),
        [MARKET_ANALYSIS_TABS.market_assumptions]: Yup.object({
            market_assumptions: requiredRichText('Assumptions'),
        }),
    },
    sector_specific_analysis: {
        [SECTOR_ANALYSIS_TABS.sector_trends]: Yup.object({
            sector_trends: requiredRichText('Sector Trends'),
        }),
        [SECTOR_ANALYSIS_TABS.institutional_context]: Yup.object({
            institutional_context: requiredRichText('Institutional Context'),
        }),
        [SECTOR_ANALYSIS_TABS.capacity_constraints]: Yup.object({
            capacity_constraints: requiredRichText('Capacity Constraints'),
        }),
        [SECTOR_ANALYSIS_TABS.regulatory_environment]: Yup.object({
            regulatory_environment: requiredRichText('Regulatory Environment'),
        }),
    },
    strategic_significance_impact_framework: {
        [STRATEGIC_SIGNIFICANCE_TABS.rationale]: Yup.object({
            strategic_infrastructure_rationale: requiredRichText('Strategic Infrastructure Rationale'),
            alignment_with_national_infrastructure_priorities: requiredRichText('Alignment with National Infrastructure Priorities'),
            contribution_to_economic_development: requiredRichText('Contribution to Economic Development'),
            long_term_strategic_significance: requiredRichText('Long-term Strategic Significance'),
            contribution_to_regional_national_development: requiredRichText('Contribution to Regional and National Development Objectives'),
        }),
        [STRATEGIC_SIGNIFICANCE_TABS.strategic_justification]: Yup.object({
            national_provincial_strategy_alignment: requiredArray('National & Provincial Strategy Alignment'),
            alignment_evidence: requiredRichText('Alignment Evidence'),
            mandate_delivery: requiredRichText('Mandate Delivery'),
            urgent_challenges: requiredArray('Urgent Challenge'),
            challenge_context: requiredRichText('Challenge Context'),
        }),
        [STRATEGIC_SIGNIFICANCE_TABS.expected_impact]: Yup.object({
            service_delivery_improvements: requiredArray('Service Delivery Improvement'),
            service_improvement_metrics: requiredRichText('Service Improvement Metrics'),
            economic_impact_areas: requiredArray('Economic Impact Area'),
            job_creation_estimate: requiredRichText('Job Creation Estimate'),
        }),
        // Every PSP field is optional.
    },
    strategic_futures_and_resilience_testing: {
        // The scenario fields are plain text; the overview question is an editor.
        [STRATEGIC_FUTURES_TABS.scenario_analysis]: Yup.object({
            resilience_justification: requiredRichText('Is this investment still justified under different future conditions?'),
            optimistic_benefits: requiredString('Benefits (Optimistic)'),
            optimistic_costs: requiredString('Costs (Optimistic)'),
            optimistic_justification: requiredString('Justification (Optimistic)'),
            mostlikely_benefits: requiredString('Benefits (Most Likely)'),
            mostlikely_costs: requiredString('Costs (Most Likely)'),
            mostlikely_justification: requiredString('Justification (Most Likely)'),
            pessimistic_benefits: requiredString('Benefits (Pessimistic)'),
            pessimistic_costs: requiredString('Costs (Pessimistic)'),
            pessimistic_justification: requiredString('Justification (Pessimistic)'),
        }),
        [STRATEGIC_FUTURES_TABS.risk_mitigation]: Yup.object({
            risk_mitigation_prevent: requiredRichText('Prevention Measures'),
            risk_mitigation_avoid: requiredRichText('Avoidance Measures'),
            risk_mitigation_transfer: requiredRichText('Transfer Mechanisms'),
            risk_mitigation_accept: requiredRichText('Acceptance & Contingency Plans'),
        }),
        [STRATEGIC_FUTURES_TABS.documents]: Yup.object({
            documents: Yup.array().min(1, 'At least one document is required').required().label('Documents'),
        }),
    },
}

// The first tab of a tab-wise step, in display order, that fails validation, with its errors.
export async function findIncompleteStepTab(stepKey: string, values: object): Promise<{ tabId: number; title: string; errors: FormikErrors<object> } | null> {
    for (const { tabId, title } of STEP_TABS[stepKey] ?? []) {
        const schema = stepTabSchemas[stepKey]?.[tabId]
        if (!schema) continue
        try {
            await schema.validate(values, { abortEarly: false })
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                return { tabId, title, errors: yupToFormErrors(error) }
            }
            throw error
        }
    }
    return null
}

export const infrastructurePlanningStageSchema = {
    project_scope: Yup.object({
        provinces: requiredNumber('Province').moreThan(0, 'Province is a required field'),
        is_land_identified: requiredString('Has the land been identified?'),
        is_land_ownership_confirmed: requiredString('Has land ownership been confirmed?'),
        is_zoning_compliant: requiredString('Is it zoning compliant?'),
        infrastructure_sector: requiredString('Sector Infrastructure Category'),
        delivery_model_initial: requiredArray('Delivery Model'),
        planning_start_date: Yup.date()
            .required('Planning Start Date is required')
            .min(
                new Date(new Date().setHours(0, 0, 0, 0)),
                'Planning Start Date cannot be in the past'
            ),
        completion_end_date: Yup.date()
            .required('Completion End Date is required')
            .min(
                Yup.ref('planning_start_date'),
                'Completion End Date cannot be before Planning Start Date'
            ),
        construction_duration_years: requiredNumber('Construction Duration Years')
            .min(0, 'Construction Duration Years cannot be negative')
            .max(100, 'Construction Duration Years cannot be greater than 100'),
        construction_duration_months: requiredNumber('Construction Duration Months')
            .min(0, 'Construction Duration Months cannot be negative')
            .max(11, 'Construction Duration Months cannot be greater than 11'),
        operations_duration_years: requiredNumber('Operation Duration Years')
            .min(0, 'Operation Duration Years cannot be negative')
            .max(100, 'Operation Duration Years cannot be greater than 100'),
        operations_duration_months: requiredNumber('Operation Duration Months')
            .min(0, 'Operation Duration Months cannot be negative')
            .max(11, 'Operation Duration Months cannot be greater than 11'),
        infrastructure_type: requiredArray('Infrastructure Type'),
        infrastructure_type_remarks: remarkPerOption('infrastructure_type'),
        infrastructure_output_type: requiredString('Infrastructure Output Type'),
        service_level: requiredString('Service Level'),
        capacity: requiredString('Capacity'),
        institutional_sponsors: Yup.array()
            .min(1, 'At least one institutional sponsor must be added')
            .required()
            .label('Institutional Sponsors'),
        stakeholder_coordination_plan: Yup.string()
            .test('has-text', 'Stakeholder Coordination Plan & Signed Agreements is a required field', hasHtmlText)
            .label('Stakeholder Coordination Plan & Signed Agreements'),
        strategic_alignment_frameworks: requiredArray('Strategic Alignment'),
        programme_alignment: requiredString('Programme Alignment'),
        related_projects: requiredArray('Related Projects'),
        fiscal_request_bfi: requiredString('Fiscal Request (BFI)').oneOf(['yes', 'no']),
        fiscal_request_reason: Yup.string().when('fiscal_request_bfi', {
            is: 'yes',
            then: () => requiredString('Reason'),
            otherwise: (schema) => schema.notRequired(),
        }),
        estimated_cost_min: requiredNumber('Estimated Cost Range - Minimum')
            .min(0, 'Estimated Cost Range - Minimum cannot be negative'),
        estimated_cost_max: requiredNumber('Estimated Cost Range - Maximum')
            .min(
                Yup.ref('estimated_cost_min'),
                'Estimated Cost Range - Maximum cannot be less than the minimum'
            ),
        basis_of_estimate: requiredArray('Basis of Estimate'),
    }),
    // The whole step; the form uses stepTabSchemas for the open tab.
    justification: Yup.object(),
    // The whole step; the form uses stepTabSchemas for the open tab.
    objectives: Yup.object(),
    supplementary_infrastructure_planning: Yup.object({
        supplementary_infrastructure_requirements: Yup.string()
            .test('has-text', 'Supplementary Infrastructure Requirements is a required field', hasHtmlText)
            .label('Supplementary Infrastructure Requirements'),
    }),
    // The whole step; the form uses stepTabSchemas for the open tab.
    strategic_significance_impact_framework: Yup.object(),
    // The whole step; the form uses stepTabSchemas for the open tab.
    sector_specific_analysis: Yup.object(),
    // The whole step; the form uses stepTabSchemas for the open tab.
    market_size_and_demand_analysis: Yup.object(),
    // The whole step; the form uses stepTabSchemas for the open tab.
    benefits_and_desired_outcomes: Yup.object(),
    investment_needs_policy_incentives: Yup.object({
        funding_requirements: requiredRichText('Funding Requirements'),
        economic_rationale: requiredRichText('Economic Rationale'),
        public_sector_intervention_justification: requiredRichText('Public Sector Intervention Justification'),
        grants_incentives_support_programmes: requiredRichText('Grants, Incentives or Support Programmes Available'),
        expected_economic_social_returns: requiredRichText('Expected Economic and Social Returns'),
    }),
    strategic_fit_summary_dashboard: Yup.object({
        alignment_with_organisational_priorities: requiredRichText('Alignment with Organisational Priorities'),
        alignment_with_sector_plan: requiredRichText('Alignment with Sector Plan'),
        alignment_with_development_strategies: requiredRichText('Alignment with Development Strategies'),
        strategic_contribution: requiredRichText('Strategic Contribution'),
    }),
    socio_economic_context: Yup.object({
        // Every field is required for the gate in the seeder, GIS datasets included.
        economic_context: requiredRichText('Economic Context'),
        economic_gis_datasets: requiredRichText('Relevant GIS Datasets — Economic'),
        social_context: requiredRichText('Social Context'),
        social_gis_datasets: requiredRichText('Relevant GIS Datasets — Social'),
        environmental_context: requiredRichText('Environmental Context'),
        environmental_gis_datasets: requiredRichText('Relevant GIS Datasets — Environmental'),
        infrastructure_baseline: requiredRichText('Infrastructure Baseline'),
        infrastructure_gis_datasets: requiredRichText('Relevant GIS Datasets — Infrastructure & Service Delivery'),
        distribution_effects: requiredRichText('Distribution Effects'),
        distribution_gis_datasets: requiredRichText('Relevant GIS Datasets — Distribution & Equity'),
    }),
    swot_analysis: Yup.object({
        swot_strengths: requiredArray('Project Strengths', 2),
        swot_weaknesses: requiredArray('Project Weaknesses', 2),
        swot_opportunities: requiredArray('External Opportunities', 2),
        swot_threats: requiredArray('External Threats', 2),
        swot_strategic_positioning_feasibility_link: requiredRichText('SWOT Strategic Positioning & Feasibility Link'),
    }),
    key_business_drivers: Yup.object({
        business_case_drivers: requiredArray('Key Business Drivers', 2),
        business_case_drivers_remarks: Yup.object().test(
            'remarks-required',
            'Remarks are required',
            (
                value?: Record<string, string>,
                context?: Yup.TestContext
            ) => {

                const selectedDrivers: string[] =
                    context?.parent?.business_case_drivers || [];

                for (const key of selectedDrivers) {

                    const plainText = value?.[key]
                        ?.replace(/<[^>]*>/g, '')
                        ?.trim();

                    if (!plainText) {
                        return context?.createError({
                            path: `${context.path}.${key}`,
                            message: 'Remark is required',
                        });
                    }
                }

                return true;
            }
        ),
    }),

    technical_options_analysis: Yup.object({
        technical_preferred_option_selected: requiredNumber("Preferred Option"),
        technical_preferred_option: requiredString("Preferred Option Justification")
    }),
    // The whole step; the form uses stepTabSchemas for the open tab.
    strategic_futures_and_resilience_testing: Yup.object(),
}

export type IInfrastructurePlanningStageSchema =
    keyof typeof infrastructurePlanningStageSchema